import { useState } from 'react';
import { MapPin, Phone, Mail, Instagram, Facebook, Linkedin, Twitter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BackButton from '@/components/BackButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    interest: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, interest: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Build lead data for the leads endpoint with general routing
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        interest: formData.interest || null,
        message: formData.message,
        source: 'general',
        form_location: 'contact_page',
        property_interest: null
      };
      
      await axios.post(`${API}/leads`, leadData);
      toast.success('Thank you! Your message has been sent successfully.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        interest: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section with Background Image */}
      <section 
        className="relative py-32 pt-40 text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1593226709004-587229db4e7c?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/65 to-black/75"></div>
        
        {/* Back Button */}
        <div className="absolute top-24 left-6 z-10">
          <BackButton />
        </div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 animate-in">Get in Touch</h1>
          <p className="text-lg sm:text-xl max-w-3xl text-gray-200 animate-in" style={{ animationDelay: '0.2s' }}>
            Whether you're an investor, tenant, or partner, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-8 text-black">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <Input
                      data-testid="contact-name-input"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <Input
                      data-testid="contact-phone-input"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-xl"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                    <Input
                      data-testid="contact-company-input"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full rounded-xl"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">I'm interested in *</label>
                    <Select onValueChange={handleSelectChange} value={formData.interest} required>
                      <SelectTrigger data-testid="contact-interest-select" className="w-full rounded-xl">
                        <SelectValue placeholder="Select your interest" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Investor">Investor Opportunities</SelectItem>
                        <SelectItem value="Tenant">Tenant Leasing</SelectItem>
                        <SelectItem value="PR">Press / Media</SelectItem>
                        <SelectItem value="Careers">Careers</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                    <Textarea
                      data-testid="contact-message-textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full rounded-xl"
                      placeholder="Tell us about your inquiry..."
                    />
                  </div>
                  <Button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={submitting}
                    size="lg"
                    className="w-full bg-[#20B2AA] hover:bg-[#189a93] text-white py-6 rounded-full text-lg font-medium"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-8 text-black">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#20B2AA]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-[#20B2AA]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-1">Address</h3>
                      <p className="text-gray-600">30 Hudson St,<br />Jersey City, NJ 07302, United States</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#20B2AA]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-[#20B2AA]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-1">Email</h3>
                      <div className="space-y-1">
                        <p className="text-gray-600">
                          <span className="text-gray-500">General:</span>{' '}
                          <a href="mailto:inquiry@gnmgt.com" className="text-[#20B2AA] hover:underline">inquiry@gnmgt.com</a>
                        </p>
                        <p className="text-gray-600">
                          <span className="text-gray-500">Rentals:</span>{' '}
                          <a href="mailto:support@gnmgt.com" className="text-[#20B2AA] hover:underline">support@gnmgt.com</a>
                        </p>
                        <p className="text-gray-600">
                          <span className="text-gray-500">Investment:</span>{' '}
                          <a href="mailto:info@gnmgt.com" className="text-[#20B2AA] hover:underline">info@gnmgt.com</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-black">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/gnmanagement_developers/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-900 hover:bg-[#20B2AA] rounded-full flex items-center justify-center text-white transition-all duration-200 hover:-translate-y-1"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a
                    href="https://www.facebook.com/GNmanagementINC"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-900 hover:bg-[#20B2AA] rounded-full flex items-center justify-center text-white transition-all duration-200 hover:-translate-y-1"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/gnmanagementinc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-900 hover:bg-[#20B2AA] rounded-full flex items-center justify-center text-white transition-all duration-200 hover:-translate-y-1"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                  <a
                    href="https://x.com/GnManagement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-900 hover:bg-[#20B2AA] rounded-full flex items-center justify-center text-white transition-all duration-200 hover:-translate-y-1"
                  >
                    <Twitter className="h-5 w-5" />
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-3xl overflow-hidden shadow-xl h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.2!2d-74.0339!3d40.7163!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c250a8e4a5e8e7%3A0x8c7f5e5e5e5e5e5e!2sGN%20Management%2C%2030%20Hudson%20St%2C%20Jersey%20City%2C%20NJ%2007302!5e0!3m2!1sen!2sus!4v1702800000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="GN Management Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
